import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

// Tipos de dados para as FAQs
export type FaqItem = {
  question: string;
  answer: string;
  category: FaqCategory;
  featured?: boolean;
};

export type FaqCategory = 
  | "financiamento" 
  | "plataforma" 
  | "pagamentos" 
  | "contratacao" 
  | "antecipacao" 
  | "suporte"
  | "all";

// Definição das categorias
export const faqCategories = [
  { id: "all", label: "Todas", description: "Todas as perguntas frequentes" },
  { id: "financiamento", label: "Financiamento", description: "Dúvidas sobre modalidades de financiamento" },
  { id: "plataforma", label: "Plataforma", description: "Como acessar e usar a plataforma" },
  { id: "pagamentos", label: "Pagamentos", description: "Informações sobre pagamentos e boletos" },
  { id: "contratacao", label: "Contratação", description: "Processo de contratação e documentação" },
  { id: "antecipacao", label: "Antecipação", description: "Dúvidas sobre antecipação de recebíveis" },
  { id: "suporte", label: "Suporte", description: "Canais e formas de atendimento" }
];

// Base completa de FAQs
export const allFaqs: FaqItem[] = [
  // Financiamento
  {
    question: "O que é a Dr.Cash?",
    answer: "A Dr.Cash é uma empresa especializada em facilitar o acesso ao crédito para tratamentos médicos, estéticos e odontológicos. Conectamos clínicas e pacientes a soluções de financiamento seguras, inovadoras e 100% digitais, tornando os tratamentos mais acessíveis e promovendo o crescimento sustentável das clínicas.",
    category: "financiamento",
    featured: true
  },
  {
    question: "Quais as modalidades de financiamento Dr.Cash?",
    answer: "Oferecemos três modalidades:\n- Padrão: Juros de 3% a 9% ao mês, pagos pelo paciente. Parcelamento de 6 a 24 vezes, sem custos adicionais para a clínica.\n- Promocional: Juros reduzidos (1,11% a 8,87%), com a clínica absorvendo uma taxa de retenção de 13,97%.\n- Especial: Paciente não paga juros; todos os custos são absorvidos pela clínica, com retenção variável entre 12,35% e 51,5% conforme o número de parcelas.",
    category: "financiamento"
  },
  {
    question: "Qual o valor mínimo e máximo disponível para financiamento?",
    answer: "O valor mínimo é R$ 250 e o máximo é R$ 30.000.",
    category: "financiamento"
  },
  {
    question: "Qual a taxa de juros das propostas?",
    answer: "As taxas variam de 1,11% a 9% ao mês, dependendo da oferta, do perfil do cliente e da quantidade de parcelas. O valor exato é informado durante a simulação da proposta.",
    category: "financiamento"
  },
  {
    question: "O que é a Taxa Operacional?",
    answer: "Porcentagem fixa cobrada sobre cada contrato repassado à clínica, conforme prazo de pagamento escolhido.",
    category: "financiamento"
  },
  
  // Plataforma
  {
    question: "Como faço login na plataforma da Dr.Cash?",
    answer: "Acesse https://drcash.com.br/novosite/acesse-portais, acesse o porta do serviço desejado e insira seu e-mail e senha cadastrados para acessar a plataforma.",
    category: "plataforma",
    featured: true
  },
  {
    question: "Esqueci minha senha de acesso à plataforma, como redefinir?",
    answer: "Clique em \"Esqueci minha senha\" na tela de login do portal, informe o e-mail cadastrado e siga as orientações recebidas por e-mail para criar uma nova senha.",
    category: "plataforma"
  },
  {
    question: "Como agendar treinamento?",
    answer: "Acesse https://l1nq.com/drcash-treinamento ou, se já tiver acesso ao novo portal, utilize a aba \"Treinamento\".",
    category: "plataforma"
  },
  
  // Pagamentos
  {
    question: "Quais os canais de atendimento do BV?",
    answer: "- CAC: 3003 7888 (capitais), 0800 777 2828 (demais localidades)\n- WhatsApp: (11) 3003-1616\n- Site: https://minha.bv.com.br/login/\n- App: https://www.bv.com.br/app-bv",
    category: "pagamentos"
  },
  {
    question: "Como renegociar parcelas em atraso?",
    answer: "O paciente deve solicitar a renegociação diretamente nos canais Parcelex:\n- E-mail: atendimento@parcelex.com.br\n- WhatsApp: (21) 98625-3551\n- Renegociação: pagamentos@parcelex.com.br\n- Central de Ajuda: ajuda.parcelex.com.br",
    category: "pagamentos"
  },
  {
    question: "Como solicitar segunda via do carnê Parcelex?",
    answer: "Acesse https://boletos.parcelex.com.br, insira o CPF do paciente e baixe o carnê. Também disponível pelo app Parcelex (Android/iOS).",
    category: "pagamentos"
  },
  
  // Contratação
  {
    question: "Por que BV, Parcelex e Dr.Cash se uniram?",
    answer: "O BV permanece como responsável pelo capital, assegurando solidez, credibilidade e segurança para a operação. A Parcelex, uma startup experiente no mercado de financiamento, cuida da inteligência de crédito, garantindo taxas de aprovação competitivas com baixos índices de inadimplência, o que proporciona maior segurança e previsibilidade para o negócio. Já a Dr.Cash é responsável pela experiência completa, oferecendo uma plataforma intuitiva e um atendimento dedicado às clínicas credenciadas, sempre focada em impulsionar o faturamento da sua base.",
    category: "contratacao"
  },
  {
    question: "Como realizar uma simulação de proposta?",
    answer: "Acesse o portal, clique em 'Nova Simulação' e preencha os dados necessários.",
    category: "contratacao",
    featured: true
  },
  {
    question: "Quais dados são necessários para simulação de crédito?",
    answer: "CPF do paciente, telefone celular (preferencialmente WhatsApp), CEP e e-mail. É fundamental garantir que as informações estejam corretas e atualizadas para evitar reprovações.",
    category: "contratacao"
  },
  {
    question: "Como anexar documentos do paciente?",
    answer: "Normalmente, não é necessário anexar documentos, exceto se solicitado pela Parcelex durante a análise. Se solicitado, o envio deve ser feito pelo canal informado (telefone ou WhatsApp).",
    category: "contratacao"
  },
  {
    question: "Como funciona a assinatura do contrato na Parcelex?",
    answer: "A assinatura é digital e deve ser feita no celular do paciente. Antes de assinar, o paciente deve revisar os valores das parcelas e total financiado. O seguro prestamista é opcional e pode ser desabilitado na tela de assinatura.",
    category: "contratacao"
  },
  
  // Antecipação
  {
    question: "O que é a antecipação de recebíveis de cartão de crédito da Dr.Cash?",
    answer: "É um serviço que permite que clínicas recebam, de forma adiantada, valores de vendas realizadas no cartão de crédito (parcelado ou à vista), melhorando o fluxo de caixa e dando mais previsibilidade financeira.",
    category: "antecipacao",
    featured: true
  },
  {
    question: "Em quanto tempo a clínica recebe a resposta da proposta?",
    answer: "O tempo de análise é geralmente de 20 a 30 minutos após a conclusão de todas as etapas (selfie, documentos, assinatura e validação de mesa). Propostas acima de R$ 5 mil podem demorar um pouco mais, devido à análise detalhada.",
    category: "antecipacao"
  },
  
  // Suporte
  {
    question: "Como entrar em contato com o suporte da Dr.Cash?",
    answer: "Você pode entrar em contato através do e-mail contato@drcash.com.br ou pelo Whatsapp +55 11 93339-8890",
    category: "suporte",
    featured: true
  }
];

interface GlobalFaqSectionProps {
  title?: string;
  subtitle?: string;
  showSearch?: boolean;
  showCategories?: boolean;
  defaultCategory?: FaqCategory;
  maxItems?: number;
  showFeaturedOnly?: boolean;
  className?: string;
  containerClass?: string;
}

const GlobalFaqSection = ({
  title = "Perguntas Frequentes",
  subtitle = "Encontre respostas para as dúvidas mais comuns sobre nossas soluções.",
  showSearch = true,
  showCategories = true,
  defaultCategory = "all",
  maxItems,
  showFeaturedOnly = false,
  className,
  containerClass
}: GlobalFaqSectionProps) => {
  const [activeCategory, setActiveCategory] = useState<FaqCategory>(defaultCategory);
  const [searchTerm, setSearchTerm] = useState("");

  // Filtra as FAQs baseado na categoria e termo de busca
  const filteredFaqs = allFaqs
    .filter(faq => 
      (activeCategory === "all" || faq.category === activeCategory) &&
      (!showFeaturedOnly || faq.featured) &&
      (faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
       faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .slice(0, maxItems);

  // Gera dados estruturados para SEO
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": filteredFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>
      
      <section className={cn("py-16 bg-white", className)}>
        <div className={cn("container mx-auto px-4 max-w-4xl", containerClass)}>
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-drcash-maastricht-blue mb-6">
              {title}
            </h2>
            {subtitle && (
              <>
                <div className="h-1 w-20 bg-gradient-to-r from-drcash-caribbean-green to-drcash-tiffany-blue rounded-full mx-auto mb-6"></div>
                <p className="text-gray-700 max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </>
            )}
          </div>
          
          {showSearch && (
            <div className="relative mb-8">
              <Input
                type="text"
                placeholder="Buscar pergunta..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            </div>
          )}
          
          {showCategories && (
            <Tabs 
              defaultValue={activeCategory} 
              className="mb-8"
              onValueChange={(value) => setActiveCategory(value as FaqCategory)}
            >
              <TabsList className="w-full flex overflow-x-auto py-2 space-x-2 bg-transparent">
                {faqCategories.map((category) => (
                  <TabsTrigger 
                    key={category.id} 
                    value={category.id}
                    className="whitespace-nowrap rounded-full"
                  >
                    {category.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
          
          {filteredFaqs.length > 0 ? (
            <Accordion type="single" collapsible className="w-full">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left font-medium text-drcash-maastricht-blue">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 whitespace-pre-line">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma pergunta encontrada para esta busca.</p>
            </div>
          )}
          
        </div>
      </section>
    </>
  );
};

export default GlobalFaqSection;
